import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const InsideAIAgentConfig = () => {
  const [agentName, setAgentName] = useState('Customer Support AI');
  const [roleInstructions, setRoleInstructions] = useState('');
  const [modelType, setModelType] = useState('GPT-4o (Latest High-Intelligence)');
  const [temperature, setTemperature] = useState(0.7);
  const [channels, setChannels] = useState({
    whatsapp: true,
    webWidget: false,
    email: false
  });
  const [chatMessages, setChatMessages] = useState([
    {
      type: 'agent',
      message: 'Hello! I am your configured agent. You can test my knowledge and persona here before deploying. How can I help?'
    },
    {
      type: 'user',
      message: 'What is our company policy on remote work according to the uploaded PDF?'
    },
    {
      type: 'agent',
      message: 'Based on the document, OrbitMind supports a hybrid work model. Employees are expected to be in-office 2 days a week, with flexible remote work for the remaining 3 days.',
      source: 'Company_Policy_v2.pdf'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');

  const documents = [
    { name: 'Company_Policy_v2.pdf', size: '1.2 MB', uploaded: '2 days ago', type: 'pdf' },
    { name: 'Pricing_Guide_2024.docx', size: '450 KB', uploaded: '5 mins ago', type: 'docx' }
  ];

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      setChatMessages([...chatMessages, { type: 'user', message: newMessage }]);
      setNewMessage('');
      // Simulate agent response
      setTimeout(() => {
        setChatMessages(prev => [...prev, { 
          type: 'agent', 
          message: 'I understand your question. Let me help you with that based on my current configuration and knowledge base.' 
        }]);
      }, 1000);
    }
  };

  return (
    <div className="bg-background-light dark:bg-background-dark text-slate-900 dark:text-white transition-colors duration-200 min-h-screen">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 flex items-center justify-between border-b border-solid border-slate-200 dark:border-[#233c48] bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md px-6 py-3">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white">
              <span className="text-xl">🚀</span>
            </div>
            <h2 className="text-lg font-extrabold leading-tight tracking-tight">OrbitMind</h2>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link className="text-slate-600 dark:text-[#92b7c9] text-sm font-medium hover:text-primary transition-colors" to="/dashboard">Dashboard</Link>
            <Link className="text-primary text-sm font-bold border-b-2 border-primary pb-1" to="/ai-agents">Agents</Link>
            <Link className="text-slate-600 dark:text-[#92b7c9] text-sm font-medium hover:text-primary transition-colors" to="/workflow-builder">Knowledge</Link>
            <Link className="text-slate-600 dark:text-[#92b7c9] text-sm font-medium hover:text-primary transition-colors" to="/services">Integrations</Link>
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 text-yellow-500 rounded-full border border-yellow-500/20">
            <span className="text-sm">ℹ️</span>
            <span className="text-xs font-bold">Unsaved Changes</span>
          </div>
          <div className="flex gap-2">
            <button className="flex min-w-[100px] cursor-pointer items-center justify-center rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold shadow-lg shadow-primary/20 hover:brightness-110 transition-all">
              <span>Save Changes</span>
            </button>
          </div>
          <div className="h-8 w-[1px] bg-slate-200 dark:bg-[#233c48]"></div>
          <div className="w-10 h-10 rounded-full border-2 border-primary/50 p-0.5">
            <div className="w-full h-full rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold text-xs">
              AD
            </div>
          </div>
        </div>
      </header>

      {/* Main Workspace */}
      <main className="flex flex-1 overflow-hidden">
        {/* Left Pane: Configuration Form */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[800px] mx-auto py-8 px-6 lg:px-12">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 mb-4">
              <Link className="text-slate-500 dark:text-[#92b7c9] hover:text-primary transition-colors text-sm font-medium flex items-center gap-1" to="/ai-agents">
                <span className="text-sm">🤖</span> Agents
              </Link>
              <span className="text-slate-400 dark:text-[#325567] font-medium">/</span>
              <span className="text-slate-900 dark:text-white text-sm font-bold">Customer Support AI</span>
            </nav>

            {/* Page Heading */}
            <div className="mb-10">
              <h1 className="text-3xl font-black tracking-tight mb-2">Agent Configuration</h1>
              <p className="text-slate-500 dark:text-[#92b7c9] text-base">Fine-tune your agent's persona, knowledge source, and intelligence parameters.</p>
            </div>

            {/* Section: Identity */}
            <section className="mb-12">
              <div className="flex items-center gap-2 border-b border-slate-200 dark:border-[#233c48] pb-3 mb-6">
                <span className="text-primary text-xl">🏷️</span>
                <h2 className="text-xl font-bold">Agent Identity</h2>
              </div>
              <div className="space-y-6">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Agent Name</label>
                  <input 
                    className="w-full rounded-lg border border-slate-200 dark:border-[#325567] bg-white dark:bg-[#192b33] p-4 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all" 
                    type="text" 
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Role / Persona Instructions</label>
                  <textarea 
                    className="w-full rounded-lg border border-slate-200 dark:border-[#325567] bg-white dark:bg-[#192b33] p-4 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all" 
                    placeholder="Act as a helpful support agent for OrbitMind..." 
                    rows="4"
                    value={roleInstructions}
                    onChange={(e) => setRoleInstructions(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 dark:text-[#92b7c9]">Define how the agent should behave and speak.</p>
                </div>
              </div>
            </section>

            {/* Section: Core Knowledge */}
            <section className="mb-12">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-[#233c48] pb-3 mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-primary text-xl">📚</span>
                  <h2 className="text-xl font-bold">Core Knowledge</h2>
                </div>
                <button className="text-xs font-bold text-primary flex items-center gap-1 hover:underline">
                  <span className="text-sm">➕</span> Add Link
                </button>
              </div>
              <div className="grid gap-3 mb-4">
                {documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-[#325567] bg-white/50 dark:bg-[#192b33]/50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${doc.type === 'pdf' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`}>
                        <span className="text-xl">{doc.type === 'pdf' ? '📄' : '📝'}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold">{doc.name}</p>
                        <p className="text-xs text-slate-500">{doc.size} • Uploaded {doc.uploaded}</p>
                      </div>
                    </div>
                    <button className="text-slate-400 hover:text-red-500 transition-colors">
                      <span className="text-xl">🗑️</span>
                    </button>
                  </div>
                ))}
              </div>
              {/* Dropzone */}
              <div className="border-2 border-dashed border-slate-200 dark:border-[#325567] rounded-xl p-8 flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors cursor-pointer group">
                <span className="text-3xl text-slate-400 group-hover:text-primary transition-colors">☁️</span>
                <p className="text-sm font-bold">Click to upload or drag & drop</p>
                <p className="text-xs text-slate-500">PDF, DOCX, TXT (Max 50MB per file)</p>
              </div>
            </section>

            {/* Section: Intelligence Settings */}
            <section className="mb-12">
              <div className="flex items-center gap-2 border-b border-slate-200 dark:border-[#233c48] pb-3 mb-6">
                <span className="text-primary text-xl">🧠</span>
                <h2 className="text-xl font-bold">Intelligence Settings</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Model Selection</label>
                  <select 
                    className="w-full rounded-lg border border-slate-200 dark:border-[#325567] bg-white dark:bg-[#192b33] p-4 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    value={modelType}
                    onChange={(e) => setModelType(e.target.value)}
                  >
                    <option>GPT-4o (Latest High-Intelligence)</option>
                    <option>GPT-3.5 Turbo (Fast & Efficient)</option>
                    <option>Claude 3.5 Sonnet (Nuanced)</option>
                    <option>Orbit-Llama 3 (Private)</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Temperature</label>
                    <span className="text-xs font-bold text-primary">{temperature} (Balanced)</span>
                  </div>
                  <input 
                    className="w-full h-2 bg-slate-200 dark:bg-[#325567] rounded-lg appearance-none cursor-pointer accent-primary" 
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={temperature}
                    onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  />
                  <div className="flex justify-between text-[10px] text-slate-500 uppercase font-bold mt-1">
                    <span>Precise</span>
                    <span>Creative</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Section: Channel Integration */}
            <section className="mb-20">
              <div className="flex items-center gap-2 border-b border-slate-200 dark:border-[#233c48] pb-3 mb-6">
                <span className="text-primary text-xl">🔗</span>
                <h2 className="text-xl font-bold">Channel Integration</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* WhatsApp Integration */}
                <label className={`relative flex flex-col gap-3 p-4 rounded-xl border-2 cursor-pointer ${channels.whatsapp ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-[#325567] bg-transparent hover:border-primary/50 transition-all'}`}>
                  <input 
                    checked={channels.whatsapp} 
                    className="absolute top-4 right-4 rounded text-primary focus:ring-0 focus:ring-offset-0" 
                    type="checkbox"
                    onChange={(e) => setChannels({...channels, whatsapp: e.target.checked})}
                  />
                  <div className="w-10 h-10 bg-green-500/20 text-green-500 flex items-center justify-center rounded-lg">
                    <span className="text-xl">💬</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold">WhatsApp</p>
                    <p className="text-[11px] text-slate-500">Business API</p>
                  </div>
                </label>

                {/* Web Widget Integration */}
                <label className={`relative flex flex-col gap-3 p-4 rounded-xl border-2 cursor-pointer ${channels.webWidget ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-[#325567] bg-transparent hover:border-primary/50 transition-all'}`}>
                  <input 
                    checked={channels.webWidget} 
                    className="absolute top-4 right-4 rounded text-primary focus:ring-0 focus:ring-offset-0" 
                    type="checkbox"
                    onChange={(e) => setChannels({...channels, webWidget: e.target.checked})}
                  />
                  <div className="w-10 h-10 bg-blue-500/20 text-blue-500 flex items-center justify-center rounded-lg">
                    <span className="text-xl">🌐</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold">Web Widget</p>
                    <p className="text-[11px] text-slate-500">Direct Embed</p>
                  </div>
                </label>

                {/* Email Integration */}
                <label className={`relative flex flex-col gap-3 p-4 rounded-xl border-2 cursor-pointer ${channels.email ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-[#325567] bg-transparent hover:border-primary/50 transition-all'}`}>
                  <input 
                    checked={channels.email} 
                    className="absolute top-4 right-4 rounded text-primary focus:ring-0 focus:ring-offset-0" 
                    type="checkbox"
                    onChange={(e) => setChannels({...channels, email: e.target.checked})}
                  />
                  <div className="w-10 h-10 bg-purple-500/20 text-purple-500 flex items-center justify-center rounded-lg">
                    <span className="text-xl">📧</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold">Email</p>
                    <p className="text-[11px] text-slate-500">Support Inbox</p>
                  </div>
                </label>
              </div>
            </section>
          </div>
        </div>

        {/* Right Pane: Agent Preview */}
        <aside className="hidden lg:flex w-[400px] flex-col border-l border-slate-200 dark:border-[#233c48] bg-white dark:bg-[#111c22]">
          <div className="p-5 flex items-center justify-between border-b border-slate-200 dark:border-[#233c48]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <h3 className="font-bold text-sm">Agent Preview</h3>
            </div>
            <button className="text-slate-400 hover:text-primary transition-colors flex items-center gap-1">
              <span className="text-sm">🔄</span>
              <span className="text-xs font-bold">Clear Chat</span>
            </button>
          </div>

          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatMessages.map((msg, index) => (
              <div key={index} className={`flex flex-col gap-1 ${msg.type === 'user' ? 'items-end' : ''}`}>
                <span className={`text-[10px] font-bold text-slate-500 uppercase ${msg.type === 'user' ? 'mr-2' : 'ml-2'}`}>
                  {msg.type === 'user' ? 'You' : 'Agent'}
                </span>
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${
                  msg.type === 'user' 
                    ? 'bg-primary text-white rounded-tr-none' 
                    : 'bg-slate-100 dark:bg-[#192b33] rounded-tl-none'
                }`}>
                  {msg.source && (
                    <div className="flex gap-1 mb-2">
                      <span className="bg-primary/20 text-primary text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                        <span className="text-[10px]">🔗</span> {msg.source}
                      </span>
                    </div>
                  )}
                  {msg.message}
                </div>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div className="p-4 border-t border-slate-200 dark:border-[#233c48]">
            <div className="relative flex items-center">
              <input 
                className="w-full rounded-full border border-slate-200 dark:border-[#325567] bg-slate-50 dark:bg-[#192b33] pl-4 pr-12 py-3 text-sm focus:ring-1 focus:ring-primary focus:border-transparent outline-none" 
                placeholder="Type a message to test..." 
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button 
                className="absolute right-1 w-9 h-9 bg-primary text-white rounded-full flex items-center justify-center shadow-lg shadow-primary/30"
                onClick={handleSendMessage}
              >
                <span className="text-xl">➤</span>
              </button>
            </div>
            <p className="text-[10px] text-center text-slate-500 mt-3 font-medium">Previewing {modelType.split(' ')[0]} with Temperature {temperature}</p>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default InsideAIAgentConfig;